import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { doc, updateDoc, collection, getDocs, deleteField } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import Modal from '../../../../components/Modal';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import SearchableSelect from '../../../../components/SearchableSelect';
import Icon from '../../../../components/Icon';
import PakistanPlaceSearch from '../../../../components/PakistanPlaceSearch';
import BranchMapPickerModal from '../../../../components/BranchMapPickerModal';
import BranchRoutePreviewMap from '../../../../components/BranchRoutePreviewMap';
import { FiEdit2, FiMapPin, FiPhone, FiHash, FiLock, FiLoader } from 'react-icons/fi';
import { fetchNearestPoliceStation } from '../../../../utils/fetchNearestPoliceStation';
import { fetchNearestAreaLabel, fetchNearbyBankNamesCsv } from '../../../../utils/fetchNearestLocationAndBanks';
import {
    BRANCH_TYPE_OPTIONS,
    COVERED_AREA_OPTIONS,
    SRA_LEVEL_OPTIONS,
    parseGeoForSubmit,
    type BranchFormData,
} from '../AddBranchModal/service/addBranchService';
import { haversineDistanceKm, formatDistanceKm } from '../../../../utils/haversineDistanceKm';
import { fetchDrivingDistanceMeters, formatDrivingKmLabel } from '../../../../utils/fetchDrivingDistanceMeters';

type LocationOption = {
    id: string;
    name: string;
    location?: { lat?: number | null; long?: number | null } | null;
};

type Branch = {
    id: string;
    bankId: string;
    branchCode: string;
    address: string;
    mobile: string;
    locationId: string;
    securityCode: string;
    locationName: string;
    psoId?: string;
    psoName: string;
    psoEmail: string;
    psoContact: string;
    name?: string;
    branchManagerId?: string;
    operationalManagerId?: string;
    areaManagerId?: string;
    branchType?: string;
    branchTypeOther?: string;
    cashLimitMillion?: number;
    atmAvailable?: boolean;
    atmMountType?: 'wall_mounted' | 'standalone' | null;
    atmSeparateFromMainEntrance?: boolean | null;
    vantagePointAvailable?: boolean;
    emergencyExitAvailable?: boolean;
    lockerAvailable?: boolean;
    coveredArea?: string;
    landmark?: string;
    nearestOtherBanksBranches?: string;
    nearestPoliceStationName?: string;
    nearestPoliceStationAddress?: string;
    nearestPoliceStationPhone?: string;
    location?: { lat?: number | null; long?: number | null; distanceFromRegion?: string };
    latitudeLongitude?: string;
    distanceFromRegion?: string;
    sraLevel?: string;
    sraReason?: string;
    eventAffectedActive?: boolean;
    eventAffectedAreasNote?: string;
    saturdayOpen?: boolean;
};

type Manager = {
    id: string;
    name: string;
    email: string;
    mobile: string;
    type?: string;
};

type PSO = {
    id: string;
    name: string;
    email?: string;
    contact?: string;
};

type BranchDetailsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    branch: Branch | null;
    managers: Record<string, Manager>;
    bankId?: string;
    onUpdate?: () => void;
    editMode?: boolean;
};

function YesNoSwitch({
    label,
    value,
    onChange,
    disabled,
}: {
    label: string;
    value: boolean;
    onChange: (v: boolean) => void;
    disabled?: boolean;
}) {
    return (
        <div className="flex min-w-0 w-full flex-col gap-1.5">
            <span className="form-field-label leading-tight">{label}</span>
            <div
                role="group"
                aria-label={label}
                className={`grid h-9 w-full grid-cols-2 overflow-hidden rounded-lg border border-gray-300 bg-gray-100 shadow-sm dark:border-gray-600 dark:bg-gray-900/40 ${
                    disabled ? 'pointer-events-none opacity-50' : ''
                }`}
            >
                <button
                    type="button"
                    onClick={() => onChange(false)}
                    className={`text-xs font-semibold tracking-wide transition ${
                        !value
                            ? 'bg-gray-600 text-white dark:bg-gray-600'
                            : 'bg-transparent text-gray-500 hover:bg-gray-200/80 dark:text-gray-400 dark:hover:bg-gray-800/80'
                    }`}
                >
                    No
                </button>
                <button
                    type="button"
                    onClick={() => onChange(true)}
                    className={`text-xs font-semibold tracking-wide transition ${
                        value
                            ? 'bg-emerald-600 text-white dark:bg-emerald-600'
                            : 'bg-transparent text-gray-500 hover:bg-gray-200/80 dark:text-gray-400 dark:hover:bg-gray-800/80'
                    }`}
                >
                    Yes
                </button>
            </div>
        </div>
    );
}

function FormRow({ children, cols = 4 }: { children: React.ReactNode; cols?: 2 | 3 | 4 | 5 }) {
    const grid =
        cols === 2
            ? 'sm:grid-cols-2'
            : cols === 3
              ? 'sm:grid-cols-2 lg:grid-cols-3'
              : cols === 5
                ? 'sm:grid-cols-2 lg:grid-cols-5'
                : 'sm:grid-cols-2 lg:grid-cols-4';
    return <div className={`grid w-full grid-cols-1 gap-3 ${grid}`}>{children}</div>;
}

function FormCell({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
    return <div className={`min-w-0 ${className}`}>{children}</div>;
}

function normStr(v: unknown): string {
    return String(v ?? '').trim();
}

function normMgrId(v: unknown): string {
    return normStr(v);
}

function cashToNumberOrNull(v: unknown): number | null {
    const t = String(v ?? '').replace(/,/g, '').trim();
    if (t === '') return null;
    const n = parseFloat(t);
    return Number.isNaN(n) ? null : n;
}

/** True if lat/lng inputs differ; ignores distance text so auto driving-distance refresh does not force a write. */
function branchCoordsDiffer(e: { latitude?: unknown; longitude?: unknown }, b: { latitude?: unknown; longitude?: unknown }) {
    const p = (s: unknown) => parseFloat(normStr(s).replace(/,/g, ''));
    const elat = p(e.latitude);
    const elong = p(e.longitude);
    const blat = p(b.latitude);
    const blng = p(b.longitude);
    const eValid = !Number.isNaN(elat) && !Number.isNaN(elong);
    const bValid = !Number.isNaN(blat) && !Number.isNaN(blng);
    if (eValid && bValid) {
        return Math.abs(elat - blat) > 1e-8 || Math.abs(elong - blng) > 1e-8;
    }
    return normStr(e.latitude) !== normStr(b.latitude) || normStr(e.longitude) !== normStr(b.longitude);
}

function geoMapsEqual(a: ReturnType<typeof parseGeoForSubmit>, b: ReturnType<typeof parseGeoForSubmit>): boolean {
    return (
        a.lat === b.lat &&
        a.long === b.long &&
        normStr(a.distanceFromRegion) === normStr(b.distanceFromRegion)
    );
}

function openMaps(latStr: string, lonStr: string) {
    const lat = parseFloat(String(latStr).replace(/,/g, '').trim());
    const lng = parseFloat(String(lonStr).replace(/,/g, '').trim());
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank', 'noopener,noreferrer');
}

/** Prefer form state; fall back to Firestore location / legacy latitudeLongitude so the route map always gets coords when stored. */
function routePreviewCoords(
    b: Branch,
    edited: Record<string, unknown> | null | undefined
): { latitude: string; longitude: string } {
    const t = (v: unknown) => String(v ?? '').trim();
    if (edited) {
        const elat = t(edited.latitude);
        const elng = t(edited.longitude);
        if (elat && elng) return { latitude: elat, longitude: elng };
    }
    if (b.location?.lat != null && b.location?.long != null) {
        return { latitude: String(b.location.lat), longitude: String(b.location.long) };
    }
    if (b.latitudeLongitude) {
        const parts = b.latitudeLongitude.split(/[,\s]+/).map((p) => p.trim()).filter(Boolean);
        if (parts.length >= 2) return { latitude: parts[0], longitude: parts[1] };
    }
    return { latitude: t(edited?.latitude), longitude: t(edited?.longitude) };
}

function branchToEditedShape(b: Branch) {
    let latitude = '';
    let longitude = '';
    let distanceFromRegion = '';
    if (b.location && (b.location.lat != null || b.location.long != null)) {
        if (b.location.lat != null) latitude = String(b.location.lat);
        if (b.location.long != null) longitude = String(b.location.long);
        distanceFromRegion = b.location.distanceFromRegion || '';
    } else if (b.latitudeLongitude) {
        const parts = b.latitudeLongitude.split(/[,\s]+/).map((p) => p.trim()).filter(Boolean);
        if (parts.length >= 2) {
            latitude = parts[0];
            longitude = parts[1];
        }
        distanceFromRegion = b.distanceFromRegion || '';
    } else {
        distanceFromRegion = b.distanceFromRegion || '';
    }

    const sr = (b.sraLevel || '').toLowerCase();
    const sraLevel: BranchFormData['sraLevel'] =
        sr === 'green' || sr === 'yellow' || sr === 'orange' || sr === 'red' ? (sr as BranchFormData['sraLevel']) : 'green';

    return {
        ...b,
        mobile: b.mobile || '',
        address: b.address || '',
        locationId: b.locationId || '',
        psoId: b.psoId || '',
        branchCode: b.branchCode || '',
        securityCode: b.securityCode || '',
        name: b.name || '',
        branchType: b.branchType || 'Branch',
        branchTypeOther: b.branchTypeOther || '',
        cashLimitMillion: b.cashLimitMillion != null ? String(b.cashLimitMillion) : '',
        atmAvailable: !!b.atmAvailable,
        atmMountType:
            b.atmMountType === 'standalone' || b.atmMountType === 'wall_mounted' ? b.atmMountType : '',
        atmSeparateFromMainEntrance:
            b.atmSeparateFromMainEntrance !== undefined && b.atmSeparateFromMainEntrance !== null
                ? !!b.atmSeparateFromMainEntrance
                : true,
        vantagePointAvailable: b.vantagePointAvailable !== undefined ? !!b.vantagePointAvailable : true,
        emergencyExitAvailable: b.emergencyExitAvailable !== undefined ? !!b.emergencyExitAvailable : true,
        lockerAvailable: b.lockerAvailable !== undefined ? !!b.lockerAvailable : true,
        coveredArea: b.coveredArea || '',
        landmark: b.landmark || '',
        nearestOtherBanksBranches: b.nearestOtherBanksBranches || '',
        nearestPoliceStationName: b.nearestPoliceStationName || '',
        nearestPoliceStationAddress: b.nearestPoliceStationAddress || '',
        nearestPoliceStationPhone: b.nearestPoliceStationPhone || '',
        latitude,
        longitude,
        distanceFromRegion,
        sraLevel: sraLevel as BranchFormData['sraLevel'],
        sraReason: b.sraReason || '',
        eventAffectedEnabled: !!(b.eventAffectedActive ?? (b.eventAffectedAreasNote && b.eventAffectedAreasNote.trim())),
        eventAffectedAreasNote: b.eventAffectedAreasNote || '',
        saturdayOpen: !!b.saturdayOpen,
    };
}

export default function BranchDetailsModal({ isOpen, onClose, branch, managers, bankId, onUpdate, editMode = false }: BranchDetailsModalProps) {
    const gmapsKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const userRole = sessionStorage.getItem('role');
    const isBankUser = userRole === 'bank';
    const isSuperAdmin = isBankUser; // Bank users are super admins
    const [isEditing, setIsEditing] = useState(editMode);
    const [loading, setLoading] = useState(false);
    const [editedBranch, setEditedBranch] = useState<any>(null);
    const [customLocationName, setCustomLocationName] = useState('');
    const [editedManagers, setEditedManagers] = useState<Record<string, Partial<Manager>>>({});
    const [mapPickerOpen, setMapPickerOpen] = useState(false);
    const [policeLookupLoading, setPoliceLookupLoading] = useState(false);

    const editBaselineRef = useRef<any>(null);
    const editManagerSnapshotsRef = useRef<
        Record<
            'branchManagerId' | 'operationalManagerId' | 'areaManagerId',
            { name: string; email: string; mobile: string } | null
        >
    >({
        branchManagerId: null,
        operationalManagerId: null,
        areaManagerId: null,
    });
    const prevIsEditingRef = useRef(false);

    const fillNearbyFromCoords = useCallback(
        async (lat: number, lng: number) => {
            const key = gmapsKey?.trim();
            if (!key) return;
            setPoliceLookupLoading(true);
            try {
                const [police, areaLabel, banksCsv] = await Promise.all([
                    fetchNearestPoliceStation(key, lat, lng),
                    fetchNearestAreaLabel(key, lat, lng),
                    fetchNearbyBankNamesCsv(key, lat, lng),
                ]);
                setEditedBranch((prev: any) =>
                    prev
                        ? {
                              ...prev,
                              nearestPoliceStationName: police?.name ?? '',
                              nearestPoliceStationAddress: police?.address ?? '',
                              nearestPoliceStationPhone: police?.phone ?? '',
                              landmark: areaLabel ?? '',
                              nearestOtherBanksBranches: banksCsv ?? '',
                          }
                        : prev
                );
            } finally {
                setPoliceLookupLoading(false);
            }
        },
        [gmapsKey]
    );

    // Reset to view mode when modal closes, or set initial edit mode when opening
    useEffect(() => {
        if (!isOpen) {
            setIsEditing(false);
        } else if (editMode && isBankUser) {
            setIsEditing(true);
        }
    }, [isOpen, editMode, isBankUser]);

    // Simplified state management
    const [locations, setLocations] = useState<LocationOption[]>([]);
    const [allManagers, setAllManagers] = useState<Manager[]>([]);
    const [psos, setPsos] = useState<PSO[]>([]);

    const mapRouteOrigin = useMemo(() => {
        if (!editedBranch?.locationId || editedBranch.locationId === 'custom') return null;
        const loc = locations.find((l) => l.id === editedBranch.locationId);
        const la = loc?.location?.lat;
        const lo = loc?.location?.long;
        if (la == null || lo == null) return null;
        const nla = Number(la);
        const nlo = Number(lo);
        if (Number.isNaN(nla) || Number.isNaN(nlo)) return null;
        return { lat: nla, lng: nlo };
    }, [editedBranch?.locationId, locations]);

    useEffect(() => {
        if (branch) {
            setEditedBranch(branchToEditedShape(branch));
            setCustomLocationName('');
            setEditedManagers({});
        }
    }, [branch, isOpen]);

    useEffect(() => {
        if (isOpen && bankId) {
            const loadData = async () => {
                try {
                    const locSnap = await getDocs(collection(db, 'locations'));
                    const locList = locSnap.docs.map((d) => ({ id: d.id, ...d.data() } as LocationOption));
                    setLocations(locList);

                    const psoSnap = await getDocs(collection(db, 'banks', bankId, 'pos'));
                    const psoList = psoSnap.docs.map(d => ({
                        id: d.id,
                        ...d.data()
                    } as PSO));
                    setPsos(psoList);

                    const mgrSnap = await getDocs(collection(db, 'banks', bankId, 'managers'));
                    const managerList = mgrSnap.docs.map(d => ({
                        id: d.id,
                        ...d.data()
                    } as Manager));
                    setAllManagers(managerList);
                } catch (err) {
                    console.error('Error loading data:', err);
                }
            };
            loadData();
        }
    }, [isOpen, bankId]);

    useEffect(() => {
        if (branch && allManagers.length > 0) {
            const updatedManagers: Record<string, Partial<Manager>> = { ...editedManagers };
            ['branchManagerId', 'operationalManagerId', 'areaManagerId'].forEach(key => {
                const mgrId = branch[key as keyof Branch] as string;
                if (mgrId && !editedManagers[key]) {
                    const foundMgr = allManagers.find(m => m.id === mgrId);
                    if (foundMgr) {
                        updatedManagers[key] = { ...foundMgr };
                    }
                }
            });
            setEditedManagers(updatedManagers);
        }
    }, [allManagers, branch, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            prevIsEditingRef.current = false;
            return;
        }
        if (isEditing && !prevIsEditingRef.current && branch) {
            editBaselineRef.current = branchToEditedShape(branch);
            const snap = (id?: string) => {
                if (!id) return null;
                const m = managers[id] || allManagers.find((x) => x.id === id);
                return m ? { name: m.name || '', email: m.email || '', mobile: m.mobile || '' } : null;
            };
            editManagerSnapshotsRef.current = {
                branchManagerId: snap(branch.branchManagerId),
                operationalManagerId: snap(branch.operationalManagerId),
                areaManagerId: snap(branch.areaManagerId),
            };
        }
        prevIsEditingRef.current = isEditing;
    }, [isOpen, isEditing, branch, managers, allManagers]);

    useEffect(() => {
        if (!isOpen || !branch) return;
        if (!isSuperAdmin || !isEditing || !editedBranch) return;

        const id = editedBranch.locationId;
        if (!id || id === 'custom') return;
        const loc = locations.find((l) => l.id === id);
        const refLat = loc?.location?.lat;
        const refLng = loc?.location?.long;
        if (refLat == null || refLng == null) return;
        const rLat = Number(refLat);
        const rLng = Number(refLng);
        if (Number.isNaN(rLat) || Number.isNaN(rLng)) return;

        const bLat = parseFloat(String(editedBranch.latitude ?? '').replace(/,/g, '').trim());
        const bLng = parseFloat(String(editedBranch.longitude ?? '').replace(/,/g, '').trim());
        if (Number.isNaN(bLat) || Number.isNaN(bLng)) return;

        const fallbackText = formatDistanceKm(haversineDistanceKm(rLat, rLng, bLat, bLng));
        const key = gmapsKey?.trim();

        let cancelled = false;
        const tid = window.setTimeout(() => {
            void (async () => {
                if (!key) {
                    if (cancelled) return;
                    setEditedBranch((prev: any) => {
                        if (!prev || prev.distanceFromRegion === fallbackText) return prev;
                        return { ...prev, distanceFromRegion: fallbackText };
                    });
                    return;
                }
                try {
                    const meters = await fetchDrivingDistanceMeters(key, { lat: rLat, lng: rLng }, { lat: bLat, lng: bLng });
                    if (cancelled) return;
                    const text = meters != null ? formatDrivingKmLabel(meters) : fallbackText;
                    setEditedBranch((prev: any) => {
                        if (!prev || prev.distanceFromRegion === text) return prev;
                        return { ...prev, distanceFromRegion: text };
                    });
                } catch {
                    if (cancelled) return;
                    setEditedBranch((prev: any) => {
                        if (!prev || prev.distanceFromRegion === fallbackText) return prev;
                        return { ...prev, distanceFromRegion: fallbackText };
                    });
                }
            })();
        }, 450);

        return () => {
            cancelled = true;
            window.clearTimeout(tid);
        };
    }, [
        isOpen,
        branch,
        isSuperAdmin,
        isEditing,
        editedBranch?.locationId,
        editedBranch?.latitude,
        editedBranch?.longitude,
        locations,
        gmapsKey,
    ]);

    if (!branch) return null;

    const handleFieldChange = (field: string, value: string | boolean) => {
        setEditedBranch((prev: any) => (prev ? { ...prev, [field]: value } : null));
    };

    const handleSave = async () => {
        if (!editedBranch || !bankId || !branch) return;

        if (isSuperAdmin) {
            const sl = editedBranch.sraLevel;
            if (
                (sl === 'orange' || sl === 'red') &&
                !String(editedBranch.sraReason || '').trim()
            ) {
                alert('Please enter a reason for Orange or Red SRA level.');
                return;
            }
        }

        const b = editBaselineRef.current ?? branchToEditedShape(branch);
        const e = editedBranch;

        setLoading(true);
        try {
            const branchRef = doc(db, 'banks', bankId, 'branch', branch.id);
            const updateData: Record<string, unknown> = {};

            if (normStr(e.mobile) !== normStr(b.mobile)) {
                updateData.mobile = normStr(e.mobile);
            }
            if (normStr(e.address) !== normStr(b.address)) {
                updateData.address = normStr(e.address);
            }

            if (isSuperAdmin) {
                if (normStr(e.branchCode) !== normStr(b.branchCode)) {
                    updateData.branchCode = normStr(e.branchCode);
                }
                if (normStr(e.securityCode) !== normStr(b.securityCode)) {
                    updateData.securityCode = normStr(e.securityCode);
                }
                if (normStr(e.name) !== normStr(b.name)) {
                    updateData.name = normStr(e.name);
                }
            }

            if (normMgrId(e.psoId) !== normMgrId(b.psoId)) {
                updateData.psoId = normMgrId(e.psoId) || null;
            }

            if (e.locationId === 'custom' && customLocationName.trim()) {
                const { addDoc, collection: col } = await import('firebase/firestore');
                const locRef = await addDoc(col(db, 'locations'), {
                    name: customLocationName.trim(),
                });
                const newLocId = locRef.id;
                if (newLocId !== normStr(b.locationId)) {
                    updateData.locationId = newLocId;
                }
            } else if (e.locationId && e.locationId !== 'custom') {
                if (normStr(e.locationId) !== normStr(b.locationId)) {
                    updateData.locationId = e.locationId;
                }
            }

            const mgrKeys = ['branchManagerId', 'operationalManagerId', 'areaManagerId'] as const;
            for (const k of mgrKeys) {
                if (normMgrId(e[k]) !== normMgrId(b[k])) {
                    updateData[k] = normMgrId(e[k]) || null;
                }
            }

            if (isSuperAdmin) {
                const btE = e.branchType || 'Branch';
                const btB = b.branchType || 'Branch';
                if (btE !== btB) {
                    updateData.branchType = btE;
                }
                const otherE = btE === 'Other' ? normStr(e.branchTypeOther) : '';
                const otherB = btB === 'Other' ? normStr(b.branchTypeOther) : '';
                if (otherE !== otherB || btE !== btB) {
                    if (btE === 'Other' && normStr(e.branchTypeOther)) {
                        updateData.branchTypeOther = normStr(e.branchTypeOther);
                    } else {
                        updateData.branchTypeOther = deleteField();
                    }
                }

                const cE = cashToNumberOrNull(e.cashLimitMillion);
                const cB = cashToNumberOrNull(b.cashLimitMillion);
                if (cE !== cB) {
                    if (cE != null) {
                        updateData.cashLimitMillion = cE;
                    } else {
                        updateData.cashLimitMillion = deleteField();
                    }
                }

                const atmE = !!e.atmAvailable;
                const atmB = !!b.atmAvailable;
                const atmSubDirty =
                    atmE &&
                    (normStr(e.atmMountType) !== normStr(b.atmMountType) ||
                        !!e.atmSeparateFromMainEntrance !== !!b.atmSeparateFromMainEntrance);
                if (atmE !== atmB || atmSubDirty) {
                    updateData.atmAvailable = atmE;
                    if (atmE) {
                        updateData.atmMountType = e.atmMountType || null;
                        updateData.atmSeparateFromMainEntrance = !!e.atmSeparateFromMainEntrance;
                    } else {
                        updateData.atmMountType = null;
                        updateData.atmSeparateFromMainEntrance = null;
                    }
                }

                if (!!e.vantagePointAvailable !== !!b.vantagePointAvailable) {
                    updateData.vantagePointAvailable = !!e.vantagePointAvailable;
                }
                if (!!e.emergencyExitAvailable !== !!b.emergencyExitAvailable) {
                    updateData.emergencyExitAvailable = !!e.emergencyExitAvailable;
                }
                if (!!e.lockerAvailable !== !!b.lockerAvailable) {
                    updateData.lockerAvailable = !!e.lockerAvailable;
                }
                if (!!e.saturdayOpen !== !!b.saturdayOpen) {
                    updateData.saturdayOpen = !!e.saturdayOpen;
                }

                const optStr = (key: keyof typeof e, bkey: keyof typeof b = key) => {
                    if (normStr(e[key]) !== normStr(b[bkey])) {
                        updateData[key as string] = normStr(e[key]) ? normStr(e[key]) : deleteField();
                    }
                };
                optStr('coveredArea');
                optStr('landmark');
                optStr('nearestOtherBanksBranches');
                optStr('nearestPoliceStationName');
                optStr('nearestPoliceStationAddress');
                optStr('nearestPoliceStationPhone');

                if (branchCoordsDiffer(e, b)) {
                    updateData.location = parseGeoForSubmit(e as BranchFormData);
                    updateData.latitudeLongitude = deleteField();
                    updateData.distanceFromRegion = deleteField();
                }

                const sraE = e.sraLevel || 'green';
                const sraB = b.sraLevel || 'green';
                if (sraE !== sraB) {
                    updateData.sraLevel = sraE;
                }
                const reasonE = normStr(e.sraReason);
                const reasonB = normStr(b.sraReason);
                if (sraE === 'orange' || sraE === 'red') {
                    if (reasonE !== reasonB || sraE !== sraB) {
                        updateData.sraReason = reasonE || null;
                    }
                } else if (reasonB || sraE !== sraB) {
                    updateData.sraReason = deleteField();
                }

                const evE = !!e.eventAffectedEnabled;
                const evB = !!b.eventAffectedEnabled;
                const noteE = normStr(e.eventAffectedAreasNote);
                const noteB = normStr(b.eventAffectedAreasNote);
                if (evE !== evB) {
                    updateData.eventAffectedActive = evE;
                }
                if (noteE !== noteB || evE !== evB) {
                    if (evE && noteE) {
                        updateData.eventAffectedAreasNote = noteE;
                    } else {
                        updateData.eventAffectedAreasNote = deleteField();
                    }
                }
            }

            if (Object.keys(updateData).length > 0) {
                await updateDoc(branchRef, updateData as any);
            }

            const snaps = editManagerSnapshotsRef.current;
            for (const k of mgrKeys) {
                const managerId = normMgrId(e[k]);
                if (!managerId) continue;
                const patch = editedManagers[k];
                const snap = snaps[k];
                if (!patch || !normStr(patch.name)) continue;
                const base =
                    snap ||
                    (() => {
                        const m = allManagers.find((x) => x.id === managerId);
                        return m
                            ? { name: m.name || '', email: m.email || '', mobile: m.mobile || '' }
                            : null;
                    })();
                if (!base) continue;
                const managerUpdateData: Record<string, string> = {};
                if (normStr(patch.name) !== normStr(base.name)) {
                    managerUpdateData.name = normStr(patch.name);
                }
                if (patch.email !== undefined && normStr(patch.email) !== normStr(base.email)) {
                    managerUpdateData.email = normStr(patch.email);
                }
                if (patch.mobile !== undefined && normStr(patch.mobile) !== normStr(base.mobile)) {
                    managerUpdateData.mobile = normStr(patch.mobile);
                }
                if (Object.keys(managerUpdateData).length > 0) {
                    await updateDoc(doc(db, 'banks', bankId, 'managers', managerId), managerUpdateData);
                }
            }

            setIsEditing(false);
            if (onUpdate) onUpdate();
            onClose();
        } catch (err: any) {
            console.error('Error updating branch:', err);
            alert('Failed to update branch: ' + (err.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        // Reset to original values
        if (branch) {
            setEditedBranch(branchToEditedShape(branch));
            const initialManagers: Record<string, Partial<Manager>> = {};
            if (branch.branchManagerId && managers[branch.branchManagerId]) {
                initialManagers.branchManagerId = { ...managers[branch.branchManagerId] };
            }
            if (branch.operationalManagerId && managers[branch.operationalManagerId]) {
                initialManagers.operationalManagerId = { ...managers[branch.operationalManagerId] };
            }
            if (branch.areaManagerId && managers[branch.areaManagerId]) {
                initialManagers.areaManagerId = { ...managers[branch.areaManagerId] };
            }
            setEditedManagers(initialManagers);
        }
        setIsEditing(false);
    };

    const handleClose = () => {
        // If editing, cancel first to reset changes
        if (isEditing) {
            handleCancel();
        }
        onClose();
    };

    const managerFields = [
        { key: 'branchManagerId', label: 'Branch Manager' },
        { key: 'operationalManagerId', label: 'Operational Manager' },
        { key: 'areaManagerId', label: 'Area Manager' },
    ] as const;

    const profileEdit = isSuperAdmin && isEditing;
    const mapPinCoords = editedBranch ? routePreviewCoords(branch, editedBranch) : { latitude: '', longitude: '' };

    return (
        <>
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Branch Details"
            size="wide"
            closeOnBackdropClick={false}
        >
            <div className="p-4 sm:p-6">
                {/* Edit Button in Header Area - Only show for super admin (bank users) */}
                {isSuperAdmin && !isEditing && (
                    <div className="mb-4 flex justify-end">
                        <button
                            onClick={() => setIsEditing(true)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-400/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-500 dark:hover:bg-gray-700 dark:hover:text-white dark:focus:ring-gray-500/25"
                            aria-label="Edit branch details"
                        >
                            <Icon icon={FiEdit2} className="h-4 w-4" />
                        </button>
                    </div>
                )}
                <div className="space-y-4">
                    {/* Same grid order as Add Branch */}
                    <FormRow cols={4}>
                        <FormCell>
                            <label className="form-field-label">Bank</label>
                            <input
                                type="text"
                                readOnly={!isEditing || !isSuperAdmin}
                                value={editedBranch?.name ?? branch?.name ?? 'N/A'}
                                onChange={(e) => handleFieldChange('name', e.target.value)}
                                className={
                                    isEditing && isSuperAdmin ? 'form-control' : 'form-control form-control-readonly'
                                }
                            />
                        </FormCell>
                        <FormCell>
                            <TextField
                                label="Branch code"
                                value={editedBranch?.branchCode ?? ''}
                                readOnly={!profileEdit}
                                onChange={(e) => handleFieldChange('branchCode', e.target.value)}
                                placeholder="Code"
                                prefix={<Icon icon={FiHash} className="text-gray-400" />}
                            />
                        </FormCell>
                        <FormCell>
                            <TextField
                                label="Client code"
                                value={editedBranch?.securityCode ?? ''}
                                readOnly={!profileEdit}
                                onChange={(e) => handleFieldChange('securityCode', e.target.value)}
                                placeholder="Client code"
                                prefix={<Icon icon={FiLock} className="text-gray-400" />}
                            />
                        </FormCell>
                        <FormCell>
                            <TextField
                                label="Mobile"
                                type="tel"
                                value={editedBranch?.mobile ?? ''}
                                readOnly={!isEditing}
                                onChange={(e) => handleFieldChange('mobile', e.target.value)}
                                placeholder="Mobile"
                                prefix={<Icon icon={FiPhone} className="text-gray-400" />}
                            />
                        </FormCell>
                    </FormRow>

                    <FormRow cols={4}>
                        <FormCell>
                            <label className="form-field-label">Branch type</label>
                            {profileEdit ? (
                                <select
                                    value={editedBranch?.branchType || 'Branch'}
                                    onChange={(e) => handleFieldChange('branchType', e.target.value)}
                                    className="form-control"
                                >
                                    {BRANCH_TYPE_OPTIONS.map((t) => (
                                        <option key={t} value={t}>
                                            {t}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    readOnly
                                    value={
                                        (editedBranch?.branchType || branch?.branchType || 'Branch') +
                                        (editedBranch?.branchType === 'Other' && editedBranch?.branchTypeOther
                                            ? ` — ${editedBranch.branchTypeOther}`
                                            : branch?.branchType === 'Other' && branch?.branchTypeOther
                                              ? ` — ${branch.branchTypeOther}`
                                              : '')
                                    }
                                    className="form-control form-control-readonly"
                                />
                            )}
                        </FormCell>
                        <FormCell>
                            <TextField
                                label="Cash limit (M PKR)"
                                value={editedBranch?.cashLimitMillion ?? ''}
                                readOnly={!profileEdit}
                                onChange={(e) => handleFieldChange('cashLimitMillion', e.target.value)}
                                placeholder="e.g. 2.5"
                            />
                        </FormCell>
                        <FormCell>
                            <label className="form-field-label">Region</label>
                            <div className="relative">
                                <Icon
                                    icon={FiMapPin}
                                    className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                                />
                                {isEditing ? (
                                    <select
                                        value={editedBranch?.locationId ?? branch?.locationId ?? ''}
                                        onChange={(e) => handleFieldChange('locationId', e.target.value)}
                                        className="form-control pl-8"
                                    >
                                        <option value="">Select region</option>
                                        {locations.map((loc) => (
                                            <option key={loc.id} value={loc.id}>
                                                {loc.name}
                                            </option>
                                        ))}
                                        <option value="custom">+ Custom region</option>
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        readOnly
                                        value={
                                            locations.find((l) => l.id === branch.locationId)?.name ||
                                            branch?.locationName ||
                                            'N/A'
                                        }
                                        className="form-control form-control-readonly pl-8"
                                    />
                                )}
                            </div>
                        </FormCell>
                        <FormCell>
                            <label className="form-field-label">Branch name</label>
                            <input
                                type="text"
                                readOnly={!isEditing}
                                value={editedBranch?.address ?? branch?.address ?? ''}
                                onChange={(e) => handleFieldChange('address', e.target.value)}
                                placeholder="Branch name"
                                className="form-control"
                            />
                        </FormCell>
                    </FormRow>

                    {profileEdit && editedBranch?.branchType === 'Other' && (
                        <FormRow cols={4}>
                            <FormCell className="lg:col-span-2">
                                <TextField
                                    label="Other branch type"
                                    value={editedBranch?.branchTypeOther || ''}
                                    onChange={(e) => handleFieldChange('branchTypeOther', e.target.value)}
                                    placeholder="Specify"
                                />
                            </FormCell>
                        </FormRow>
                    )}

                    {isEditing && editedBranch?.locationId === 'custom' && (
                        <FormRow cols={4}>
                            <FormCell className="lg:col-span-2">
                                <TextField
                                    label="Custom region"
                                    value={customLocationName}
                                    onChange={(e) => setCustomLocationName(e.target.value)}
                                    placeholder="Region name"
                                />
                            </FormCell>
                        </FormRow>
                    )}

                    <FormRow cols={4}>
                        <FormCell>
                            <YesNoSwitch
                                label="Vantage point"
                                value={!!editedBranch?.vantagePointAvailable}
                                onChange={(v) => handleFieldChange('vantagePointAvailable', v)}
                                disabled={!profileEdit}
                            />
                        </FormCell>
                        <FormCell>
                            <YesNoSwitch
                                label="Emergency exit"
                                value={!!editedBranch?.emergencyExitAvailable}
                                onChange={(v) => handleFieldChange('emergencyExitAvailable', v)}
                                disabled={!profileEdit}
                            />
                        </FormCell>
                        <FormCell>
                            <YesNoSwitch
                                label="Saturday open"
                                value={!!editedBranch?.saturdayOpen}
                                onChange={(v) => handleFieldChange('saturdayOpen', v)}
                                disabled={!profileEdit}
                            />
                        </FormCell>
                        <FormCell>
                            <YesNoSwitch
                                label="Locker"
                                value={!!editedBranch?.lockerAvailable}
                                onChange={(v) => handleFieldChange('lockerAvailable', v)}
                                disabled={!profileEdit}
                            />
                        </FormCell>
                    </FormRow>

                    <FormRow cols={3}>
                        <FormCell className="flex flex-col justify-end">
                            <YesNoSwitch
                                label="ATM"
                                value={!!editedBranch?.atmAvailable}
                                onChange={(v) =>
                                    setEditedBranch((prev: any) =>
                                        prev
                                            ? {
                                                  ...prev,
                                                  atmAvailable: v,
                                                  ...(!v
                                                      ? { atmMountType: '', atmSeparateFromMainEntrance: true }
                                                      : {}),
                                              }
                                            : null
                                    )
                                }
                                disabled={!profileEdit}
                            />
                        </FormCell>
                        <FormCell className="flex flex-col justify-end">
                            <div
                                className={
                                    editedBranch?.atmAvailable
                                        ? ''
                                        : 'invisible pointer-events-none select-none'
                                }
                                aria-hidden={!editedBranch?.atmAvailable}
                            >
                                <label className="form-field-label">ATM type</label>
                                {profileEdit ? (
                                    <select
                                        value={editedBranch?.atmMountType || ''}
                                        onChange={(e) => handleFieldChange('atmMountType', e.target.value)}
                                        className="form-control"
                                        tabIndex={editedBranch?.atmAvailable ? 0 : -1}
                                    >
                                        <option value="">—</option>
                                        <option value="wall_mounted">Wall mounted</option>
                                        <option value="standalone">Standalone</option>
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        readOnly
                                        className="form-control form-control-readonly text-sm"
                                        value={
                                            editedBranch?.atmMountType === 'wall_mounted'
                                                ? 'Wall mounted'
                                                : editedBranch?.atmMountType === 'standalone'
                                                  ? 'Standalone'
                                                  : '—'
                                        }
                                    />
                                )}
                            </div>
                        </FormCell>
                        <FormCell className="flex flex-col justify-end">
                            <div
                                className={
                                    editedBranch?.atmAvailable
                                        ? ''
                                        : 'invisible pointer-events-none select-none'
                                }
                                aria-hidden={!editedBranch?.atmAvailable}
                            >
                                <YesNoSwitch
                                    label="Separate entrance"
                                    value={!!editedBranch?.atmSeparateFromMainEntrance}
                                    onChange={(v) => handleFieldChange('atmSeparateFromMainEntrance', v)}
                                    disabled={!profileEdit}
                                />
                            </div>
                        </FormCell>
                    </FormRow>

                    <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6 lg:items-end">
                        <FormCell className="min-w-0 sm:col-span-2 lg:col-span-2">
                            {profileEdit ? (
                                <PakistanPlaceSearch
                                    apiKey={gmapsKey}
                                    onResolved={(p) => {
                                        handleFieldChange('latitude', String(p.lat));
                                        handleFieldChange('longitude', String(p.lng));
                                        void fillNearbyFromCoords(p.lat, p.lng);
                                    }}
                                />
                            ) : (
                                <div>
                                    <label className="form-field-label">Find location (Pakistan)</label>
                                    <input
                                        type="text"
                                        readOnly
                                        value="—"
                                        className="form-control form-control-readonly text-sm"
                                    />
                                </div>
                            )}
                        </FormCell>
                        <FormCell>
                            <label className="form-field-label">Long</label>
                            <input
                                type="text"
                                inputMode="decimal"
                                readOnly={!profileEdit}
                                value={editedBranch?.longitude ?? ''}
                                onChange={(e) => handleFieldChange('longitude', e.target.value)}
                                className="form-control text-sm"
                            />
                        </FormCell>
                        <FormCell>
                            <label className="form-field-label">Lat</label>
                            <input
                                type="text"
                                inputMode="decimal"
                                readOnly={!profileEdit}
                                value={editedBranch?.latitude ?? ''}
                                onChange={(e) => handleFieldChange('latitude', e.target.value)}
                                className="form-control text-sm"
                            />
                        </FormCell>
                        <FormCell>
                            <label className="form-field-label">Distance from region</label>
                            <input
                                type="text"
                                readOnly={!profileEdit}
                                value={editedBranch?.distanceFromRegion || ''}
                                onChange={(e) => handleFieldChange('distanceFromRegion', e.target.value)}
                                placeholder={profileEdit ? 'Auto (km)' : ''}
                                className="form-control text-sm"
                            />
                        </FormCell>
                        <FormCell className="flex flex-col justify-end lg:w-auto">
                            <span className="form-field-label opacity-0 select-none">Map</span>
                            <button
                                type="button"
                                title={gmapsKey?.trim() ? 'Pick on map' : 'Open in Google Maps'}
                                disabled={
                                    !profileEdit && (!mapPinCoords.latitude || !mapPinCoords.longitude)
                                }
                                onClick={() => {
                                    if (profileEdit && gmapsKey?.trim()) setMapPickerOpen(true);
                                    else openMaps(mapPinCoords.latitude, mapPinCoords.longitude);
                                }}
                                className="inline-flex h-9 w-full max-w-[2.25rem] items-center justify-center rounded-lg border border-gray-300 bg-gray-100 text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-40 dark:border-gray-600 dark:bg-black/30 dark:text-gray-200 dark:hover:bg-black/50"
                            >
                                <Icon icon={FiMapPin} className="h-4 w-4" />
                            </button>
                        </FormCell>
                    </div>

                    {!isEditing && editedBranch && branch && (
                        <BranchRoutePreviewMap
                            apiKey={gmapsKey}
                            routeOrigin={mapRouteOrigin}
                            locationName={editedBranch.locationName || branch.locationName || ''}
                            locationId={String(editedBranch.locationId || branch.locationId || '')}
                            {...routePreviewCoords(branch, editedBranch)}
                        />
                    )}

                    {policeLookupLoading && profileEdit && (
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                <Icon icon={FiLoader} className="h-3.5 w-3.5 animate-spin" />
                                Looking up…
                            </span>
                        </div>
                    )}

                    <FormRow cols={3}>
                        <FormCell>
                            <label className="form-field-label">Covered area</label>
                            {profileEdit ? (
                                <select
                                    value={editedBranch?.coveredArea || ''}
                                    onChange={(e) => handleFieldChange('coveredArea', e.target.value)}
                                    className="form-control"
                                >
                                    <option value="">—</option>
                                    {COVERED_AREA_OPTIONS.filter(Boolean).map((o) => (
                                        <option key={o} value={o}>
                                            {o}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    readOnly
                                    value={editedBranch?.coveredArea || '—'}
                                    className="form-control form-control-readonly"
                                />
                            )}
                        </FormCell>
                        <FormCell>
                            <TextField
                                label="Landmark"
                                value={editedBranch?.landmark || ''}
                                readOnly={!profileEdit}
                                onChange={(e) => handleFieldChange('landmark', e.target.value)}
                                placeholder="Area / landmark"
                            />
                        </FormCell>
                        <FormCell>
                            <TextField
                                label="Nearest banks"
                                value={editedBranch?.nearestOtherBanksBranches || ''}
                                readOnly={!profileEdit}
                                onChange={(e) => handleFieldChange('nearestOtherBanksBranches', e.target.value)}
                                placeholder="Meezan, UBL…"
                            />
                        </FormCell>
                    </FormRow>

                    <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-900/30">

                        <div className="w-full">
                            <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                <div>
                                    <label className="form-field-label">Police Station name</label>
                                    <input
                                        type="text"
                                        readOnly={!profileEdit}
                                        value={editedBranch?.nearestPoliceStationName || ''}
                                        onChange={(e) => handleFieldChange('nearestPoliceStationName', e.target.value)}
                                        className={
                                            profileEdit
                                                ? 'form-control max-w-full text-sm'
                                                : 'form-control form-control-readonly max-w-full text-sm'
                                        }
                                    />
                                </div>
                                <div>
                                    <label className="form-field-label">Police Station address</label>
                                    <input
                                        type="text"
                                        readOnly={!profileEdit}
                                        value={editedBranch?.nearestPoliceStationAddress || ''}
                                        onChange={(e) => handleFieldChange('nearestPoliceStationAddress', e.target.value)}
                                        className={
                                            profileEdit
                                                ? 'form-control max-w-full text-sm'
                                                : 'form-control form-control-readonly max-w-full text-sm'
                                        }
                                    />
                                </div>
                                <div>
                                    <label className="form-field-label">Police Station phone</label>
                                    <input
                                        type="text"
                                        readOnly={!profileEdit}
                                        value={editedBranch?.nearestPoliceStationPhone || ''}
                                        onChange={(e) => handleFieldChange('nearestPoliceStationPhone', e.target.value)}
                                        className={
                                            profileEdit
                                                ? 'form-control max-w-full text-sm'
                                                : 'form-control form-control-readonly max-w-full text-sm'
                                        }
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="w-full max-w-xl">
                            <span className="form-field-label">SRA level</span>
                            {profileEdit ? (
                                <div className="mt-1 flex flex-wrap gap-2">
                                    {SRA_LEVEL_OPTIONS.map((opt) => (
                                        <label
                                            key={opt.value}
                                            className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-2 py-1 text-xs transition dark:border-gray-600 ${
                                                editedBranch?.sraLevel === opt.value
                                                    ? 'border-gray-500 bg-white ring-1 ring-gray-400 dark:bg-gray-800'
                                                    : 'border-gray-200 bg-gray-50 dark:bg-gray-900/40'
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="sra-details"
                                                checked={editedBranch?.sraLevel === opt.value}
                                                onChange={() =>
                                                    setEditedBranch((prev: any) =>
                                                        prev
                                                            ? {
                                                                  ...prev,
                                                                  sraLevel: opt.value,
                                                                  sraReason:
                                                                      opt.value === 'orange' || opt.value === 'red'
                                                                          ? prev.sraReason
                                                                          : '',
                                                              }
                                                            : null
                                                    )
                                                }
                                                className="sr-only"
                                            />
                                            <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${opt.dotClass}`} />
                                            <span className="font-medium text-gray-800 dark:text-gray-200">{opt.label}</span>
                                            <span className="text-gray-500 dark:text-gray-400">({opt.hint})</span>
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <div className="mt-1">
                                    {(() => {
                                        const level = (editedBranch?.sraLevel || 'green') as BranchFormData['sraLevel'];
                                        const opt =
                                            SRA_LEVEL_OPTIONS.find((o) => o.value === level) ?? SRA_LEVEL_OPTIONS[0];
                                        return (
                                            <span className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800">
                                                <span className={`h-2.5 w-2.5 rounded-full ${opt.dotClass}`} />
                                                <span className="font-medium text-gray-800 dark:text-gray-200">
                                                    {opt.label}
                                                </span>
                                                <span className="text-gray-500 dark:text-gray-400">({opt.hint})</span>
                                            </span>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>

                        {(profileEdit
                            ? editedBranch?.sraLevel === 'orange' || editedBranch?.sraLevel === 'red'
                            : editedBranch?.sraLevel === 'orange' ||
                              editedBranch?.sraLevel === 'red' ||
                              !!(editedBranch?.sraReason && String(editedBranch.sraReason).trim())) && (
                            <div className="w-full max-w-md">
                                <label className="form-field-label">SRA reason</label>
                                <textarea
                                    readOnly={!profileEdit}
                                    rows={2}
                                    value={editedBranch?.sraReason || ''}
                                    onChange={(e) => handleFieldChange('sraReason', e.target.value)}
                                    className={`resize-y py-2 text-sm max-w-full ${
                                        profileEdit ? 'form-control' : 'form-control form-control-readonly'
                                    }`}
                                />
                            </div>
                        )}

                        <YesNoSwitch
                            label="Event-affected"
                            value={!!editedBranch?.eventAffectedEnabled}
                            onChange={(v) =>
                                setEditedBranch((prev: any) =>
                                    prev
                                        ? {
                                              ...prev,
                                              eventAffectedEnabled: v,
                                              ...(!v ? { eventAffectedAreasNote: '' } : {}),
                                          }
                                        : null
                                )
                            }
                            disabled={!profileEdit}
                        />

                        {(profileEdit ? editedBranch?.eventAffectedEnabled : !!editedBranch?.eventAffectedEnabled) && (
                            <div className="w-full max-w-md">
                                <label className="form-field-label">Affected areas note</label>
                                <textarea
                                    readOnly={!profileEdit}
                                    rows={2}
                                    value={editedBranch?.eventAffectedAreasNote || ''}
                                    onChange={(e) => handleFieldChange('eventAffectedAreasNote', e.target.value)}
                                    className={`resize-y py-2 text-sm max-w-full ${
                                        profileEdit ? 'form-control' : 'form-control form-control-readonly'
                                    }`}
                                />
                            </div>
                        )}
                    </div>

                    <FormRow cols={4}>
                        <FormCell>
                            <label className="form-field-label">PSO assign</label>
                            {isEditing ? (
                                <select
                                    value={editedBranch?.psoId || ''}
                                    onChange={(e) => handleFieldChange('psoId', e.target.value)}
                                    className="form-control"
                                >
                                    <option value="">Select</option>
                                    {psos.map((pso) => (
                                        <option key={pso.id} value={(pso as any).psoId || pso.id}>
                                            {pso.name}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    readOnly
                                    value={branch?.psoName || 'N/A'}
                                    className="form-control form-control-readonly"
                                />
                            )}
                        </FormCell>
                    </FormRow>

                    {/* Manager Information */}
                    {managerFields.map(({ key, label }) => {
                        const currentMgrId = editedBranch?.[key] ?? branch?.[key] ?? '';
                        // Look for manager in both managers prop and allManagers state
                        const currentMgr = currentMgrId
                            ? (managers[currentMgrId] || allManagers.find(m => m.id === currentMgrId) || null)
                            : null;
                        const editedMgr = editedManagers[key] || currentMgr;

                        return (
                            <div
                                key={key}
                                className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/30"
                            >
                                <h6 className="mb-4 text-sm font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    {label}
                                </h6>
                                <div className="space-y-4">
                                    {isEditing ? (
                                        <SearchableSelect
                                            options={allManagers.map(mgr => ({
                                                id: mgr.id,
                                                label: mgr.name,
                                                value: mgr.id,
                                                details: {
                                                    email: mgr.email,
                                                    mobile: mgr.mobile,
                                                },
                                            }))}
                                            value={currentMgrId || ''}
                                            onChange={(value) => {
                                                handleFieldChange(key, value);
                                                const selectedMgr = allManagers.find(m => m.id === value);
                                                if (selectedMgr) {
                                                    setEditedManagers(prev => ({
                                                        ...prev,
                                                        [key]: { ...selectedMgr }
                                                    }));
                                                } else {
                                                    setEditedManagers(prev => ({
                                                        ...prev,
                                                        [key]: {}
                                                    }));
                                                }
                                            }}
                                            placeholder={`Select ${label} (Optional)`}
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            readOnly
                                            value={currentMgr ? currentMgr.name : 'No manager assigned'}
                                            className="form-control form-control-readonly"
                                        />
                                    )}
                                    {currentMgr && (
                                        <div className="space-y-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                                            <div>
                                                <label className="mb-1.5 block text-sm font-normal text-gray-500 dark:text-gray-400">
                                                    Name
                                                </label>
                                                <input
                                                    type="text"
                                                    readOnly={!isEditing}
                                                    value={editedMgr?.name || currentMgr.name || ''}
                                                    onChange={(e) => setEditedManagers(prev => ({
                                                        ...prev,
                                                        [key]: { ...prev[key], name: e.target.value }
                                                    }))}
                                                    className="form-control"
                                                />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-sm font-normal text-gray-500 dark:text-gray-400">
                                                    Email
                                                </label>
                                                <input
                                                    type="email"
                                                    readOnly={!isEditing}
                                                    value={editedMgr?.email || currentMgr.email || ''}
                                                    onChange={(e) => setEditedManagers(prev => ({
                                                        ...prev,
                                                        [key]: { ...prev[key], email: e.target.value }
                                                    }))}
                                                    className="form-control"
                                                />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-sm font-normal text-gray-500 dark:text-gray-400">
                                                    Mobile
                                                </label>
                                                <input
                                                    type="text"
                                                    readOnly={!isEditing}
                                                    value={editedMgr?.mobile || currentMgr.mobile || ''}
                                                    onChange={(e) => setEditedManagers(prev => ({
                                                        ...prev,
                                                        [key]: { ...prev[key], mobile: e.target.value }
                                                    }))}
                                                    className="form-control"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    {isEditing ? (
                        <>
                            <Button variant="secondary" onClick={handleCancel} disabled={loading}>
                                Cancel
                            </Button>
                            <Button onClick={handleSave} disabled={loading}>
                                {loading ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </>
                    ) : (
                        <Button variant="secondary" onClick={handleClose}>
                            Close
                        </Button>
                    )}
                </div>
            </div>
        </Modal>
        <BranchMapPickerModal
            isOpen={mapPickerOpen}
            onClose={() => setMapPickerOpen(false)}
            apiKey={gmapsKey}
            routeOrigin={mapRouteOrigin}
            latitude={editedBranch?.latitude}
            longitude={editedBranch?.longitude}
            onSubmit={(lat, lng) => {
                setEditedBranch((prev: any) =>
                    prev ? { ...prev, latitude: String(lat), longitude: String(lng) } : null
                );
                void fillNearbyFromCoords(lat, lng);
            }}
        />
        </>
    );
}
